import { useState } from "react";
import { toast } from "sonner";
import { Mail, RefreshCw, Edit3 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { onboardingApi, ApiError } from "@/lib/api";

// Step 1B — "Check your inbox". The verify link in the email goes to
// /onboarding/verify?token=... which StepVerifyToken handles authoritatively.
// This card is purely informational with a resend button (backend re-mails
// the same token-based link).
export function StepVerifyWaiting() {
  const { user } = useAuth();
  const email = useOnboardingStore((s) => s.email);
  const onboardingId = useOnboardingStore((s) => s.onboardingId);
  const reset = useOnboardingStore((s) => s.reset);

  const [resending, setResending] = useState(false);

  const resend = async () => {
    if (!onboardingId) {
      toast.error("Please restart onboarding");
      return;
    }
    setResending(true);
    try {
      // Backend returns email_sent only when a live provider accepted the
      // email. We surface that honestly — never claim "re-sent" for a noop
      // sender or a network failure.
      const res = await onboardingApi.resendVerification(onboardingId);
      if (res?.email_sent) {
        toast.success("Verification email re-sent", {
          description: `New link sent to ${email}.`,
        });
      } else if (res?.dev_verify_url) {
        toast.message("Dev mode — email not actually sent", {
          description: `Provider ${res.email_provider}; use the verify URL from the server logs.`,
        });
      } else {
        toast.error("Couldn't resend", {
          description: "We couldn't deliver the email. Please try again in a moment.",
        });
      }
    } catch (err) {
      const ae = err as ApiError;
      console.error("resendVerification", ae);
      const description =
        ae.code === "email_provider_unavailable"
          ? "Verification email service is temporarily down. Please try again in a few minutes or contact support."
          : ae.code === "email_send_failed"
          ? "We couldn't deliver the email. Please try again in a moment."
          : ae.status === 0
          ? "We couldn't reach VirtualDesk. Check your connection and try again."
          : ae.message || "Please try again.";
      toast.error("Couldn't resend", { description });
    } finally {
      setResending(false);
    }
  };

  const changeEmail = () => {
    reset();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet">
            <Mail className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>Check your inbox</CardTitle>
            <CardDescription>
              We sent a verification link to{" "}
              <span className="font-mono text-foreground">
                {email ?? user?.email ?? "your inbox"}
              </span>
              . The link expires in 24 hours. Click it to continue.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-border bg-surface-2/40 px-4 py-3 text-xs text-muted-foreground">
          Click the link in your inbox to continue. Closing this tab is fine — the
          link opens the next step.
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          <button
            type="button"
            onClick={resend}
            disabled={resending}
            className="inline-flex items-center gap-1.5 text-brand-violet hover:text-brand-violet/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded disabled:opacity-60"
          >
            <RefreshCw className={resending ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
            {resending ? "Resending…" : "Resend"}
          </button>
          <button
            type="button"
            onClick={changeEmail}
            className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded"
          >
            <Edit3 className="h-3.5 w-3.5" />
            Change email
          </button>
        </div>

        <div>
          <Button variant="outline" size="sm" onClick={resend} disabled={resending}>
            Resend verification email
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
